import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { OnboardingFormValues } from "../OnboardingFormSchema";
import { useWizard } from "../FormWizardContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { CheckCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

export interface ReviewStepProps {
  form: UseFormReturn<OnboardingFormValues>;
  onSubmit: (values: OnboardingFormValues) => void;
  formId?: string;
  isSubmitting: boolean;
}

export const ReviewStep = ({ 
  form, 
  onSubmit, 
  formId, 
  isSubmitting 
}: ReviewStepProps) => {
  const { currentStep, totalSteps } = useWizard();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Check if this step should be rendered
  if (!((totalSteps === 2 && currentStep === 2) || 
        (totalSteps === 4 && currentStep === 4) ||
        (totalSteps === 5 && currentStep === 5))) {
    return null;
  }
  
  const values = form.getValues();
  
  // Determine which phase we're in based on the number of steps
  const isPhase1 = totalSteps === 4; // Phase 1 has 4 steps
  const isPhase3 = totalSteps === 2; // Phase 3 has 2 steps
  
  const handleSubmit = async () => {
    setIsProcessing(true);
    
    try {
      if (!formId) {
        throw new Error("Form ID is missing");
      }
      
      console.log("Current review phase:", isPhase1 ? "Phase 1" : isPhase3 ? "Phase 3" : "Other phase");
      
      // Phase 1: Creator info submission
      if (isPhase1) {
        console.log("Submitting Phase 1 data with form values:", values);
        
        // Prepare update data with proper field mapping and formatting
        const updateData = {
          kol_name: values.creator_name || '',
          social_media_handler: values.social_media_handler || '',
          social_media_type: values.social_media_type || '',
          social_media_name: values.social_media_name || '',
          agreed_rate: values.agreed_rate || 0,
          currency_type: values.currency_type || 'USD',
          region: values.region || '',
          content_links: Array.isArray(values.content_links) ? values.content_links : [],
          experience_rate: values.experience_rate || null,
          email: values.email || '',
          deliverables: values.deliverables || '',
          feedback: values.feedback || '',
          start_date: values.start_date ? values.start_date.toISOString().split('T')[0] : null,
          end_date: values.end_date ? values.end_date.toISOString().split('T')[0] : null,
          pic: Array.isArray(values.pic) ? values.pic : [],
          status_phase_1: 'completed',
          status_phase_2: 'in-progress',
          status_phase_3: 'pending',
          status_phase_4: 'pending', 
          status_phase_5: 'pending',
          status_phase_6: 'pending',
          status_phase_7: 'pending',
          approved: 'pending',
          test_transfer: false,
          test_transfer_url: null,
          tx_hash: null,
          payment_status: 'pending'
        };

        console.log("Sending properly formatted update data:", updateData);

        const { data, error } = await supabase.functions.invoke('update-payment-submissions', {
          body: { 
            form_id: formId,
            update_data: updateData
          }
        });

        if (error) {
          console.error("Error updating form:", error);
          throw new Error(`Error updating form: ${error.message}`);
        }

        console.log("Form update successful:", data);
        
        // Navigate to success page
        navigate(`/onboarding-success/${formId}`);
        
      } 
      // Phase 3: Invoice submission
      else if (isPhase3) {
        console.log("Submitting Phase 3 invoice data with values:", values);
        
        // Prepare invoice data with proper formatting
        const invoiceData = {
          invoice_number: values.invoice_number || '',
          invoice_date: values.invoice_date ? values.invoice_date.toISOString().split('T')[0] : null,
          invoice_link: values.invoice_link || '',
          wallet_address: values.wallet_address || '',
          wallet_address_network: values.wallet_address_network || '',
          chain: values.chain || '',
          amount: values.amount || 0,
          description: values.description || '',
          status_phase_3: 'completed',
          status_phase_4: 'in-progress'
        };
        
        console.log("Submitting invoice data via edge function:", invoiceData);
        
        const { data, error } = await supabase.functions.invoke('update-payment-submissions', {
          body: {
            form_id: formId,
            update_data: invoiceData
          }
        });
        
        if (error) {
          console.error("Error submitting invoice data:", error);
          throw new Error(`Error submitting invoice: ${error.message}`);
        }
        
        console.log("Invoice submission successful:", data);
        
        // Navigate to the success page
        navigate(`/onboarding-success/${formId}`);
      }
      // Other phases - use standard submission
      else {
        console.log("Submitting form data for other phase");
        onSubmit(values);
      }
    } catch (error: any) {
      console.error("Error in form submission:", error);
      // Still try the standard submission as fallback if custom handling fails
      if (!isPhase3) {
        onSubmit(values);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold">Review Your Information</h3>
      <p className="text-muted-foreground">Review all information before submitting.</p>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="bg-green-50 dark:bg-green-900/20 p-4 flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h3 className="text-lg font-medium text-green-800 dark:text-green-300">
              {isPhase3 ? "Invoice Ready for Submission" : "Application Ready for Submission"}
            </h3>
          </div>

          <div className="p-6 space-y-6">
            {/* Creator Information */}
            <div>
              <h4 className="font-medium mb-2">Creator Information</h4>
              <Separator className="mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Creator Name</p>
                  <p className="font-medium">{values.creator_name || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{values.email || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Social Media Type</p>
                  <p className="font-medium">{values.social_media_type || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Social Media Handle</p>
                  <p className="font-medium">{values.social_media_handler || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Social Media Name</p>
                  <p className="font-medium">{values.social_media_name || "Not provided"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Region</p>
                  <p className="font-medium">{values.region || "Not provided"}</p>
                </div>
              </div>
            </div>

            {/* Compensation Information */}
            <div>
              <h4 className="font-medium mb-2">Compensation Information</h4>
              <Separator className="mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Agreed Rate</p>
                  <p className="font-medium">
                    {values.agreed_rate ? `${values.agreed_rate} ${values.currency_type || ""}` : "Not provided"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Collaboration Period</p>
                  <p className="font-medium">
                    {values.start_date ? format(values.start_date, "PP") : "Not specified"} - 
                    {values.end_date ? format(values.end_date, "PP") : " Ongoing"}
                  </p>
                </div>
              </div>
            </div>

            {/* Invoice Information - only show in Phase 3 */}
            {isPhase3 && (
              <div>
                <h4 className="font-medium mb-2">Invoice Information</h4>
                <Separator className="mb-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Invoice Number</p>
                    <p className="font-medium">{values.invoice_number || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Invoice Date</p>
                    <p className="font-medium">
                      {values.invoice_date ? format(values.invoice_date, "PP") : "Not provided"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Wallet Address</p>
                    <p className="font-medium break-all text-sm">{values.wallet_address || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Network</p>
                    <p className="font-medium">{values.wallet_address_network || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Chain</p>
                    <p className="font-medium">{values.chain || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Amount</p>
                    <p className="font-medium">
                      {values.amount ? `${values.amount} ${values.currency_type || ""}` : "Not provided"}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="font-medium whitespace-pre-wrap">{values.description || "Not provided"}</p>
                </div>
                {values.invoice_link && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">Generated Invoice</p>
                    <a 
                      href={values.invoice_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center mt-1"
                    >
                      View Invoice
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center pt-4">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || isProcessing}
          className="min-w-[200px]"
        >
          {isSubmitting || isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isProcessing ? "Processing..." : "Submitting..."}
            </>
          ) : (
            isPhase3 ? "Submit Invoice" : "Submit Application"
          )}
        </Button>
      </div>
    </div>
  );
}
